<template>
  <div
    :style="{ ...this.wrapperStyle, ...this.wrapperStyles }"
    v-if="this.bufferParams"
  >
    <v-stage
      :config="{ ...stageConfig, ...stageView }"
      ref="stage"
      @wheel="onWheel"
    >
      <v-layer>
        <v-group ref="content">
          <v-line
            v-if="this.bufferParams?.lines?.length"
            v-for="(wall, index) in this.bufferParams.lines"
            :key="wall.floor_buffer_id"
            style="cursor: pointer;"
            :config="{
              points: this.flattenPoints(wall.points),
              stroke: this.getLineColor(wall, `buffer-${index}`, wall.floor_flat_id),
              strokeWidth: isRevit ? 1 : 40,
              x: 0,
              y: 0,
              lineJoin: 'round',
            }"
            @mouseenter="onMouseEnter(wall, $event, `buffer-${index}`)"
            @mousemove="onMouseMove(wall, $event, `buffer-${index}`)"
            @mouseleave="onMouseLeave"
            v-show="wall.points && wall.points.length > 0"
          />
          <v-circle
            v-if="!!this.bufferParams?.circles?.length"
            v-for="(circle, index) in this.bufferParams?.circles"
            :config="{
              x: circle.points[0],
              y: circle.points[1],
              radius: 50,
              opacity: '1',
              stroke: '#000',
              strokeWidth: 30,
            }"
            @mouseenter="onMouseEnter(circle, $event, `circle-${index}`)"
            @mousemove="onMouseMove(circle, $event, `circle-${index}`)"
            @mouseleave="onMouseLeave"
            v-show="circle.points && circle.points.length >= 2"
          />
        </v-group>
      </v-layer>
    </v-stage>
    <div
      v-if="showTooltip && hoveredWall && hoveredWall.wall_type_name"
      class="wall-type-tooltip"
      :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
    >
      <div class="tooltip-content">
        <strong>Тип стены:</strong> <br />
        {{ hoveredWall.wall_type_name }}
      </div>
    </div>
  </div>
  <NotFoundText v-else />
</template>

<script>
import NotFoundText from "@/components/common/NotFoundText.vue";
import { onKonvaWheel } from '@/views/BuildingSettings/services';
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';

export default {
  name: "Schema",
  components: { NotFoundText },
  props: [
    'bufferParams',
    'containerWidth',
    'containerHeight',
    'rotationAngle',
    'isRevit'
  ],
  data() {
    return {
      stageConfig: {
        draggable: true,
        scaleX: 1,
        scaleY: 1,
      },
      wrapperStyle: {
        position: 'relative',
      },
      hoveredIndex: null,
      hoveredWall: null,
      showTooltip: false,
      tooltipX: 0,
      tooltipY: 0,
    };
  },
  computed: {
    getBoundsFromBuffer() {
      const xs = [];
      const ys = [];

      const collect = (arr) => {
        arr?.forEach(obj => {
          if (obj.points && Array.isArray(obj.points) && obj.points.length > 0) {
            obj.points.forEach(point => {
              if (Array.isArray(point) && point.length >= 2) {
                const [x, y] = point;
                if (typeof x === 'number' && typeof y === 'number' && !isNaN(x) && !isNaN(y)) {
                  xs.push(x);
                  ys.push(y);
                }
              }
            });
          }
        });
      };

      collect(this.bufferParams?.lines);

      if (this.bufferParams?.circles?.length) {
        this.bufferParams.circles.forEach(c => {
          if (c.points && Array.isArray(c.points) && c.points.length >= 2) {
            const [x, y] = c.points;
            if (typeof x === 'number' && typeof y === 'number' && !isNaN(x) && !isNaN(y)) {
              xs.push(x);
              ys.push(y);
            }
          }
        });
      }

      if (!xs.length || !ys.length) return null;

      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const width = maxX - minX;
      const height = maxY - minY;

      return {
        x: minX,
        y: minY,
        width: Math.max(width, 1),
        height: Math.max(height, 1)
      };
    },
    stageView() {
      const width = this.containerWidth || 450;
      const height = this.containerHeight || 450;

      return {
        width: width,
        height: height,
      }
    },
    wrapperStyles() {
      const width = this.containerWidth || 450;
      const height = this.containerHeight || 450;

      return {
        width: `${width}px`,
        height: `${height}px`,
      }
    },
  },
  methods: {
    onKonvaWheel,
    flattenPoints(pointsArray) {
      return pointsArray.reduce((acc, [x, y]) => {
        acc.push(x, y);
        return acc;
      }, []);
    },
    onMouseLeave(evt) {
      this.hoveredWall = null;
      this.hoveredIndex = null;
      this.showTooltip = false;
      this.tooltipX = 0;
      this.tooltipY = 0;
      const stage = evt.target.getStage();
      stage.container().style.cursor = 'default';
    },
    onMouseEnter(wall, evt, index) {
      this.hoveredIndex = index
      this.hoveredWall = wall;

      const stage = evt.target.getStage();
      const stageBox = stage.container().getBoundingClientRect();
      const pointerPos = stage.getPointerPosition();

      let tooltipX = stageBox.left + pointerPos.x + 10;
      let tooltipY = stageBox.top + pointerPos.y - 10;

      const tooltipWidth = 200;
      const screenWidth = window.innerWidth;

      if (tooltipX + tooltipWidth > screenWidth) {
        tooltipX = stageBox.left + pointerPos.x - tooltipWidth - 10;
      }

      if (tooltipY < 0) {
        tooltipY = stageBox.top + pointerPos.y + 20;
      }

      this.tooltipX = Math.max(10, tooltipX);
      this.tooltipY = Math.max(10, tooltipY);

      this.showTooltip = wall.wall_type_name ? true : false;

      stage.container().style.cursor = 'pointer';
    },
    onMouseMove(wall, evt, index) {
      if (this.showTooltip && wall.wall_type_name) {
        const stage = evt.target.getStage();
        const stageBox = stage.container().getBoundingClientRect();
        const pointerPos = stage.getPointerPosition();

        let tooltipX = stageBox.left + pointerPos.x + 10;
        let tooltipY = stageBox.top + pointerPos.y - 10;

        const tooltipWidth = 200;
        const screenWidth = window.innerWidth;

        if (tooltipX + tooltipWidth > screenWidth) {
          tooltipX = stageBox.left + pointerPos.x - tooltipWidth - 10;
        }

        if (tooltipY < 0) {
          tooltipY = stageBox.top + pointerPos.y + 20;
        }

        this.tooltipX = Math.max(10, tooltipX);
        this.tooltipY = Math.max(10, tooltipY);
      }
    },
    getCircleSettings(room) {
      const { flat_num } = room
      const flatNumLength = String(flat_num).length
      let settings = {
        x: room.center_x + 50,
        y: room.center_y + 17,
        radius: 60,
        listening: false
      }
      if (flatNumLength === 1) {
        settings.x = room.center_x + 40
        settings.radius = 55
      } else if (flatNumLength === 3) {
        settings.x = room.center_x + 61
        settings.radius = 67
      }
      return {
        ...settings,
        fill: '#fff',
        opacity: '.7',
        stroke: '#ccc',
        strokeWidth: 1
      }
    },
    getLineColor(wall, index) {
      if (this.hoveredIndex === index) return 'blue'
      if (!wall.floor_param_id && this.activeBufferObjectId === wall.floor_buffer_id) return '#970e0e'
      if (!wall.floor_param_id) return '#000'

      return wall?.floor_param_type_color || '#000'
    },
    onWheel(e) {
      const stage = this.$refs.stage.getNode();
      this.onKonvaWheel(e, stage)
    },
    centerFitRotate() {
      const stage = this.$refs.stage.getNode();
      const group = this.$refs.content?.getNode();
      if (!stage || !group) return;

      const W = stage.width();
      const H = stage.height();

      const bounds = this.getBoundsFromBuffer;
      if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
        group.rotation(0);
        group.scale({ x: 1, y: 1 });
        group.position({ x: W / 2, y: H / 2 });
        group.offset({ x: 0, y: 0 });
        stage.batchDraw();
        return;
      }

      group.rotation(0);
      group.scale({ x: 1, y: 1 });
      group.position({ x: 0, y: 0 });
      group.offset({ x: 0, y: 0 });

      const rect = group.getClientRect({ skipTransform: true, skipStroke: false });

      if (!rect.width || !rect.height) {
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;

        const padding = 0.85;
        const scaleX = (W * padding) / bounds.width;
        const scaleY = (H * padding) / bounds.height;
        const scale = Math.min(scaleX, scaleY, 2);

        group.scale({ x: scale, y: scale });
        group.offset({ x: centerX, y: centerY });
        group.position({ x: W / 2, y: H / 2 });

        group.rotation(this.rotationAngle || 0);

        stage.batchDraw();
        return;
      }

      const padding = 0.85;
      const scaleX = (W * padding) / rect.width;
      const scaleY = (H * padding) / rect.height;
      const scale = Math.min(scaleX, scaleY, 2);

      group.scale({ x: scale, y: scale });

      const centerX = rect.x + rect.width / 2;
      const centerY = rect.y + rect.height / 2;
      group.offset({ x: centerX, y: centerY });
      group.position({ x: W / 2, y: H / 2 });

      group.rotation(this.rotationAngle || 0);

      const rectAfter = group.getClientRect();
      if (rectAfter.width && rectAfter.height) {
        const fixX = W / 2 - (rectAfter.x + rectAfter.width / 2);
        const fixY = H / 2 - (rectAfter.y + rectAfter.height / 2);
        group.x(group.x() + fixX);
        group.y(group.y() + fixY);
      }

      stage.batchDraw();
    },
  },
  watch: {
    rotationAngle: { immediate: true, handler() { this.$nextTick(this.centerFitRotate); } },
    containerWidth() { this.$nextTick(this.centerFitRotate); },
    containerHeight() { this.$nextTick(this.centerFitRotate); },
    bufferParams: { deep: true, handler() { this.$nextTick(this.centerFitRotate); } },
  },
  mounted() {
    this.$nextTick(this.centerFitRotate);
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar()
    return { showSucceedSnackbar }
  }
};
</script>

<style scoped>
.wall-type-tooltip {
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(30, 30, 30, 0.9));
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  transform: translateY(-100%);
  margin-top: -5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
  max-width: 200px;
  word-wrap: break-word;
  white-space: normal;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-100%) scale(0.9);
  }

  to {
    opacity: 1;
    transform: translateY(-100%) scale(1);
  }
}

.tooltip-content {
  max-width: 250px;
  word-wrap: break-word;
  white-space: normal;
}

.tooltip-content strong {
  font-weight: 600;
}
</style>
